import clsx from 'clsx';
import React from 'react';

import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { ColorPicker } from '@/src/globalComponents';

import TagPill from './TagPill';

import { TagType } from '../types';

interface EditTagFormProps {
  tagInput: string;
  setTagInput: (value: string) => void;
  newTagColor: string;
  setNewTagColor: (color: string) => void;
  showColorPicker: boolean;
  setShowColorPicker: (show: boolean) => void;
  onSave: (label: string, color: string) => void;
  editTag: TagType;
}

const EditTagForm: React.FC<EditTagFormProps> = ({
  tagInput,
  setTagInput,
  newTagColor,
  setNewTagColor,
  showColorPicker,
  setShowColorPicker,
  onSave,
}) => {
  const colorPickerRef = React.useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = React.useState(false);

  return (
    <>
      <input
        type="text"
        placeholder="Edit tag"
        value={tagInput}
        onChange={(e) => setTagInput(e.target.value)}
        disabled={isSaving}
        className={clsx(
          'tw-w-full',
          'tw-border-cfo-grey-600',
          'tw-rounded-md',
          'tw-mx-1',
          'tw-px-2',
          'tw-py-2',
          'tw-text-md',
          'tw-my-4',
          'focus:tw-outline-none',
          'focus:tw-ring-2',
          'focus:tw-ring-blue-500'
        )}
      />
      <div
        className={clsx('tw-flex', 'tw-items-start', 'tw-my-3', 'tw-relative', 'tw-min-h-[16px]')}
      >
        {/* Tag Preview */}
        <div className={clsx('tw-flex', 'tw-flex-wrap', 'tw-max-w-[200px]', 'tw-gap-2')}>
          <TagPill
            tag={{
              id: tagInput + '-preview',
              name: tagInput,
              color: newTagColor || '#bdbdbd',
            }}
            className={clsx('tw-py-1', 'tw-rounded-full', 'tw-text-sm')}
            style={{ backgroundColor: newTagColor || '#bdbdbd' }}
          />
        </div>

        {/* Controls */}
        <div
          className={clsx('tw-flex', 'tw-items-center', 'tw-gap-2', 'tw-ml-auto')}
          style={{ minHeight: 32 }}
        >
          {/* Palette Icon - always at end of flex */}
          <button
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
            aria-label="Pick tag color"
          >
            <Icon
              icon={ICONS.palette}
              className="tw-text-[16px]"
              style={{ position: 'absolute', top: '6px', right: '60px' }}
            />
          </button>
          {showColorPicker && (
            <div
              ref={colorPickerRef}
              style={{
                width: 100,
                minWidth: 100,
                maxWidth: 100,
                zIndex: 3,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <ColorPicker
                onSelect={(colorObj) => setNewTagColor(colorObj.hex)}
                color={newTagColor}
                value={newTagColor}
                forceShowPicker
              />
            </div>
          )}
          {/* Save Button */}
          <button
            onClick={() => {
              if (tagInput.trim()) {
                setIsSaving(true);
                onSave(tagInput.trim(), newTagColor);
                setShowColorPicker(false);
              }
            }}
            disabled={!tagInput.trim() || isSaving}
            className={clsx(
              'tw-px-4',
              'tw-py-2',
              'tw-rounded-md',
              'tw-text-white',
              'tw-text-sm',
              tagInput.trim() && !isSaving
                ? 'tw-bg-green-500 hover:tw-bg-green-600'
                : 'tw-bg-gray-300 tw-cursor-not-allowed'
            )}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
};

export default EditTagForm;
